import { useMemo, useState } from 'react'

import { api } from '../api/client'
import type { LivelloPermesso, TenantApiKey, TenantApiKeyCreated, TenantApiKeyIn } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

const RUOLO_LABEL: Record<string, string> = {
  tenant_admin: 'Amministratore',
  tenant_operator: 'Operatore',
  read_only: 'Sola lettura',
}

function etichettaLivello(l: LivelloPermesso): string {
  return { write: 'Scrittura', read: 'Lettura', disabled: 'Disattivato', hidden: 'Nascosto' }[l]
}

/** Il livello effettivo di un modulo nella bozza (la chiave esplicita, o il
 *  default `*`, o «lettura»); un ruolo di sola lettura non concede scrittura. */
function livello(perm: Record<string, LivelloPermesso>, chiave: string, base: string): LivelloPermesso {
  const l = perm[chiave] ?? perm['*'] ?? 'read'
  return base === 'read_only' && l === 'write' ? 'read' : l
}

function bozzaIniziale(): TenantApiKeyIn {
  return { name: '', base_role: 'tenant_operator', permissions: { '*': 'read' }, expires_at: null, note: null }
}

/**
 * Chiavi API del tenant: un sistema esterno (SIEM, SOC, automazione) legge e
 * scrive i dati del tenant via API, col profilo per modulo che decidi qui. Il
 * segreto si vede una volta sola. Alcune superfici (segreti, utenti, altre
 * chiavi, amministrazione) restano off-limits per qualunque chiave. Enterprise.
 */
export default function ChiaviApi() {
  const dati = useAsync(() => api.tenantApiKeys(), [])
  const d = dati.data
  const [bozza, setBozza] = useState<TenantApiKeyIn>(bozzaIniziale())
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [creata, setCreata] = useState<TenantApiKeyCreated | null>(null)

  const livelli: LivelloPermesso[] = d?.catalog.levels ?? ['hidden', 'disabled', 'read', 'write']
  const ruoli = d?.catalog.base_roles ?? ['tenant_admin', 'tenant_operator', 'read_only']

  function setLivello(chiave: string, l: LivelloPermesso) {
    setBozza((b) => ({ ...b, permissions: { ...b.permissions, [chiave]: l } }))
  }

  async function crea() {
    setBusy(true)
    setErrore(null)
    setCreata(null)
    try {
      const k = await api.createTenantApiKey({ ...bozza, name: bozza.name.trim() })
      setCreata(k)
      setBozza(bozzaIniziale())
      dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function revoca(id: string) {
    if (!confirm(t('Revocare questa chiave? Chi la usa perde subito l’accesso.'))) return
    await api.revokeTenantApiKey(id)
    dati.reload()
  }

  const selettore = (chiave: string) => (
    <select
      value={livello(bozza.permissions, chiave, bozza.base_role)}
      onChange={(e) => setLivello(chiave, e.target.value as LivelloPermesso)}
    >
      {livelli.map((l) => (
        <option key={l} value={l}>{t(etichettaLivello(l))}</option>
      ))}
    </select>
  )

  const moduli = useMemo(() => d?.catalog.groups ?? [], [d])

  // A revoked or expired key stays in the list, dimmed.
  const spenta = (k: TenantApiKey) => (k.active ? undefined : { opacity: 0.5 })
  const statoChiave = (k: TenantApiKey) => (k.active ? t('attiva') : k.revoked_at ? t('revocata') : t('scaduta'))
  const colonne: Column<TenantApiKey>[] = [
    { key: 'name', header: t('Nome'), render: (k) => <span style={spenta(k)}>{k.name}</span> },
    { key: 'prefix', header: t('Prefisso'), render: (k) => <code style={spenta(k)}>{k.prefix}…</code> },
    { key: 'base_role', header: t('Ruolo'), value: (k) => t(RUOLO_LABEL[k.base_role] ?? k.base_role), render: (k) => (
      <span style={spenta(k)}>{t(RUOLO_LABEL[k.base_role] ?? k.base_role)}</span>
    ) },
    { key: 'stato', header: t('Stato'), value: statoChiave, render: (k) => <span style={spenta(k)}>{statoChiave(k)}</span> },
    { key: 'last_used_at', header: t('Ultimo uso'), type: 'date', value: (k) => k.last_used_at ?? '', render: (k) => (
      <span style={spenta(k)}>{k.last_used_at ? formatDateTime(k.last_used_at) : '—'}</span>
    ) },
    { key: 'expires_at', header: t('Scadenza'), type: 'date', value: (k) => k.expires_at ?? '', render: (k) => (
      <span style={spenta(k)}>{k.expires_at ? formatDateTime(k.expires_at) : t('nessuna')}</span>
    ) },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (k) => (
      k.active && <button className="secondary" onClick={() => void revoca(k.id)}>{t('Revoca')}</button>
    ) },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Chiavi API')}</h1>
          <p className="page__subtitle">
            {t('Chiavi per far leggere e scrivere i dati di questo tenant a un sistema esterno (SIEM, SOC, automazione), con il profilo per modulo che decidi tu. Il segreto si mostra una volta sola.')}
          </p>
        </div>
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>{t('Aggiorna')}</button>
      </header>

      <ErrorBanner error={dati.error} />

      {dati.loading && !d ? <Spinner /> : !d ? null : (
        <>
          {creata && (
            <Card title={t('Chiave creata')}>
              <p className="field__hint">{t('Copiala adesso: non sarà più visibile dopo aver lasciato questa pagina.')}</p>
              <pre className="codice-segreto" style={{ userSelect: 'all', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{creata.secret}</pre>
              <button className="secondary" onClick={() => { navigator.clipboard?.writeText(creata.secret); }}>{t('Copia')}</button>
            </Card>
          )}

          <Card title={t('Nuova chiave API')}>
            <ErrorBanner error={errore} />
            <div className="campo">
              <label htmlFor="ak-name">{t('Nome')}</label>
              <input id="ak-name" value={bozza.name} onChange={(e) => setBozza((b) => ({ ...b, name: e.target.value }))}
                placeholder={t('es. SIEM, export notturno')} />
            </div>
            <div className="campo">
              <label>{t('Ruolo di partenza')}</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ruoli.map((r) => (
                  <button key={r} type="button"
                    className={bozza.base_role === r ? '' : 'secondary'}
                    onClick={() => setBozza((b) => ({ ...b, base_role: r }))}>
                    {t(RUOLO_LABEL[r] ?? r)}
                  </button>
                ))}
              </div>
              <p className="field__hint">{t('Il profilo può solo restringere rispetto al ruolo di partenza: non concede mai più di quello.')}</p>
            </div>
            <div className="campo">
              <label htmlFor="ak-exp">{t('Scadenza (facoltativa)')}</label>
              <input id="ak-exp" type="date"
                onChange={(e) => setBozza((b) => ({ ...b, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null }))} />
            </div>

            <h4>{t('Profilo: cosa può vedere e fare la chiave')}</h4>
            <div className="campo">
              <label>{t('Tutti i moduli (predefinito)')}</label>
              {selettore('*')}
            </div>
            {moduli.map((g) => (
              <fieldset key={g.key} style={{ border: '1px solid var(--border)', borderRadius: 8, margin: '8px 0', padding: 8 }}>
                <legend>{g.label}</legend>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
                  {g.modules.map((m) => (
                    <div key={m.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span>{m.label}</span>
                      {selettore(m.key)}
                    </div>
                  ))}
                </div>
              </fieldset>
            ))}
            <button onClick={() => void crea()} disabled={busy || !bozza.name.trim()}>
              {busy ? t('Creo…') : t('Crea chiave')}
            </button>
          </Card>

          <Card title={t('Chiavi esistenti')}>
            {d.keys.length === 0 ? (
              <EmptyState title={t('Nessuna chiave API.')} hint={t('Creane una qui sopra per far accedere un sistema esterno.')} />
            ) : (
              <DataTable rows={d.keys} columns={colonne} rowKey={(k) => k.id} conteggio={{ nome: t('chiavi') }} />
            )}
          </Card>
        </>
      )}
    </>
  )
}
